import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Send, Check, Sparkles } from "lucide-react";
import LoadingSpinner from "../../../../../common/LoadingSpinner";
import { llmAPI } from "../../../../../../services/api";

const clamp = (s) => (s ?? "").toString();

function TypingText({ text, speed = 12, onDone }) {
    const [shown, setShown] = useState("");
    const idxRef = useRef(0);
    const timerRef = useRef(null);

    useEffect(() => {
        setShown("");
        idxRef.current = 0;

        const full = clamp(text);
        if (!full) {
        onDone?.();
        return;
        }

        timerRef.current = window.setInterval(() => {
        idxRef.current += 1;
        setShown(full.slice(0, idxRef.current));
        if (idxRef.current >= full.length) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
            onDone?.();
        }
        }, speed);

        return () => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
        };
    }, [text, speed, onDone]);

    return <div className="whitespace-pre-wrap">{shown}</div>;
    }

    export default function RewriteChatModal({
    open,
    onClose,
    initialText,
    onApply, // (finalText) => void
    }) {
    const [messages, setMessages] = useState([]); // [{role:"user"|"assistant", text, typing?}]
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [typingDone, setTypingDone] = useState(true);

    const initialTextRef = useRef("");

    const lastAssistant = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i]?.role === "assistant") return messages[i]?.text || "";
        }
        return "";
    }, [messages]);

    const baseForNextTurn = useMemo(() => {
        // Use the latest assistant output as the base, otherwise the captured initial text.
        const la = (lastAssistant || "").trim();
        const init = clamp(initialTextRef.current).trim();
        return la ? la : init;
    }, [lastAssistant]);

    useEffect(() => {
        if (!open) return;


        const t = clamp(initialText).trim();
        initialTextRef.current = t;

        setMessages([]);
        setInput("");
        setBusy(false);
        setTypingDone(true);

        // Auto-run initial rewrite if there is text
        if (t) {
        (async () => {
            setBusy(true);
            setTypingDone(false);
            setMessages([{ role: "user", text: "Rewrite this (fix grammar/clarity, keep meaning)." }]);

            try {
            // NOTE: matches your current usage shape. If your llmAPI expects (text, style, keep_meaning),
            // adjust this call accordingly.
            const res = await llmAPI.rewrite(t, {
                style: "professional",
                keep_meaning: true,
                instruction: "",
            });
            const rewritten = res?.data?.rewritten ?? "";

            setMessages((prev) => [
                ...prev,
                { role: "assistant", text: clamp(rewritten), typing: true },
            ]);
            } catch (e) {
            setMessages((prev) => [
                ...prev,
                {
                role: "assistant",
                text: e?.response?.data?.detail || "Rewrite failed. Please try again.",
                typing: false,
                },
            ]);
            setTypingDone(true);
            } finally {
            setBusy(false);
            }
        })();
        } else {
        setMessages([
            {
            role: "assistant",
            text:
                "Type your comments in the main box first, then open this to rewrite. " +
                "Or paste text into the main comments box and re-open.",
            typing: false,
            },
        ]);
        }
    }, [open]); // ✅ FIX: remove initialText from dependencies

    const handleTypingDone = useCallback(() => {
        setTypingDone(true);
    }, [])
    const sendFollowUp = async () => {
        const q = clamp(input).trim();
        if (!q || busy) return;

        setInput("");
        setBusy(true);
        setTypingDone(false);

        setMessages((prev) => [...prev, { role: "user", text: q }]);

        try {
        const res = await llmAPI.rewrite(baseForNextTurn, {
            style: "professional",
            keep_meaning: true,
            instruction: q,
        });
        const rewritten = res?.data?.rewritten ?? "";

        setMessages((prev) => [...prev, { role: "assistant", text: clamp(rewritten), typing: true }]);
        } catch (e) {
        setMessages((prev) => [
            ...prev,
            {
            role: "assistant",
            text: e?.response?.data?.detail || "Rewrite failed. Please try again.",
            typing: false,
            },
        ]);
        setTypingDone(true);
        } finally {
        setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[80]">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />

        <div className="absolute left-1/2 top-1/2 w-[96vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
                <div className="text-sm text-gray-500">SmokyQ-AI Rewrite</div>
                <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Rewrite Comments
                </div>
            </div>

            <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-md hover:bg-gray-100"
                aria-label="Close"
            >
                <X className="w-5 h-5" />
            </button>
            </div>

            {/* Messages */}
            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-3">
            {messages.map((m, idx) => {
                const isUser = m.role === "user";
                return (
                <div key={idx} className={["flex", isUser ? "justify-end" : "justify-start"].join(" ")}>
                    <div
                    className={[
                        "max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap",
                        isUser ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900",
                    ].join(" ")}
                    >
                    {m.role === "assistant" && m.typing ? (
                    <TypingText text={m.text} speed={10} onDone={handleTypingDone} />
                    ) : (
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    )}
                    </div>
                </div>
                );
            })}

            {busy && (
                <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-900 rounded-2xl px-3 py-2 text-sm inline-flex items-center gap-2">
                    <LoadingSpinner size="small" />
                    <span>Thinking…</span>
                </div>
                </div>
            )}
            </div>

            {/* Composer */}
            <div className="border-t p-3">
            <div className="flex gap-2">
                <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder='Ask a follow-up, e.g. "Make it shorter" or "Use a more formal tone"'
                className="flex-1 border rounded-md px-3 py-2 text-sm"
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendFollowUp();
                    }
                }}
                disabled={busy}
                />
                <button
                type="button"
                onClick={sendFollowUp}
                disabled={busy || !clamp(input).trim()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gray-900 text-white hover:bg-black disabled:opacity-60"
                title="Send"
                >
                <Send className="w-4 h-4" />
                Send
                </button>
            </div>

            <div className="mt-2 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                Rewrite only: fixes grammar/clarity and keeps meaning.
                </div>

                <button
                type="button"
                onClick={() => onApply?.(baseForNextTurn)}
                disabled={!baseForNextTurn || busy || !typingDone}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-xs"
                title={!typingDone ? "Wait for typing to finish" : "Apply to comments"}
                >
                <Check className="w-4 h-4" />
                Apply to Comments
                </button>
            </div>
            </div>
        </div>
        </div>
    );
    }
